import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { addressAPI } from '../services/api';
import LocalStorageService from '../services/localStorageService';
import { useAuth } from './AuthContext';

const AddressDataContext = createContext();

// Memory optimization constants - limit arrays to prevent bloat
const MAX_TRANSACTIONS = 25;
const MAX_APPROVALS = 25;
const MAX_EVENTS = 25;
const MAX_BALANCES = 50;
const MAX_NFTS = 50;

// Trim array helper
const trimArray = (arr, max) => Array.isArray(arr) ? arr.slice(0, max) : [];

// Trim chain data to reduce memory footprint
const trimChainData = (chainData) => {
    if (!chainData) return {};
    return {
        ...chainData,
        balances: trimArray(chainData.balances, MAX_BALANCES),
        transactions: trimArray(chainData.transactions, MAX_TRANSACTIONS),
        transactionRefs: trimArray(chainData.transactionRefs, MAX_TRANSACTIONS),
        approvals: trimArray(chainData.approvals, MAX_APPROVALS),
        events: trimArray(chainData.events, MAX_EVENTS),
        nftHoldings: trimArray(chainData.nftHoldings, MAX_NFTS)
    };
};

export const useAddressData = () => {
    const context = useContext(AddressDataContext);
    if (!context) {
        throw new Error('useAddressData must be used within AddressDataProvider');
    }
    return context;
};

export const AddressDataProvider = ({ children }) => {
    const [addresses, setAddresses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const isDataLoadingRef = useRef(false);
    // Stale-while-revalidate: pages key their skeletons off `isLoading`, so a
    // background stale-refresh must NOT flip it once we have data — otherwise
    // every tab switch after the 5-min stale window blanks the page for the
    // full duration of the slow /addresses/all call.
    const hasDataRef = useRef(false);
    useEffect(() => {
        hasDataRef.current = addresses.length > 0;
    }, [addresses]);
    
    // Track active progressive update loops for cancellation
    const activeUpdatesRef = useRef(new Map()); // address -> { cancelled: boolean }

    // Get authentication context
    const { isAuthenticated, loading: authLoading } = useAuth();

    // Helper function to process address data from new schema
    const processAddressData = (addr) => {
        if (!addr || typeof addr !== 'object') {
            return null;
        }

        // Detect Bitcoin address
        const isBtcAddress = addr.network === 'bitcoin' || addr.chainType === 'Bitcoin' ||
            /^(1|3|bc1)/i.test(addr.address);

        // ── Bitcoin addresses: simplified processing ──
        if (isBtcAddress && addr.chains?.bitcoin) {
            const btcChain = addr.chains.bitcoin;
            const btcBalances = Array.isArray(btcChain.balances) ? btcChain.balances : [];
            const totalValue = btcBalances.reduce((sum, b) => sum + (parseFloat(b.valueUsd) || 0), 0);
            const events = Array.isArray(btcChain.events) ? btcChain.events : [];
            const txRefs = Array.isArray(btcChain.transactionRefs) ? btcChain.transactionRefs : [];
            const recentSeed = Array.isArray(addr.recentTransactions) ? addr.recentTransactions : [];
            const seededEvents = [
                ...recentSeed.map((tx) => ({
                    txHash: tx.txHash,
                    hash: tx.txHash,
                    from: tx.from,
                    to: tx.to,
                    network: tx.network || 'bitcoin',
                    chain: tx.network || 'bitcoin',
                    blockNumber: tx.blockNumber,
                    valueUsd: tx.valueUsd,
                    value: tx.value,
                    type: tx.txType || 'transaction_update',
                    eventType: tx.txType || 'transaction_update',
                    timestamp: tx.timestamp,
                })),
                ...events,
            ];

            return {
                ...addr,
                chains: {
                    bitcoin: trimChainData({
                        balances: btcBalances,
                        approvals: [],
                        nftHoldings: [],
                        nftAssets: [],
                        events: seededEvents,
                        transactionRefs: txRefs,
                        lastUpdated: btcChain.lastUpdated
                    })
                },
                balances: {},
                approvals: {},
                nfts: {},
                events: { bitcoin: trimArray(seededEvents, MAX_EVENTS) },
                transactions: trimArray(seededEvents, MAX_TRANSACTIONS),
                recentTransactions: trimArray(recentSeed, 10),
                totalValue,
                lastActivity: seededEvents.length > 0 ? seededEvents[0].timestamp : null,
                stats: {
                    totalTransactions: txRefs.length || seededEvents.length,
                    totalTokens: btcBalances.length,
                    totalNfts: 0,
                    totalApprovals: 0,
                    totalValue,
                    lastActivity: seededEvents.length > 0 ? seededEvents[0].timestamp : null
                },
                nativeBalance: {
                    balance: btcBalances[0]?.balance || '0',
                    balanceEth: parseFloat(btcBalances[0]?.balance) || 0,
                    symbol: 'BTC'
                }
            };
        }

        // ── EVM addresses: existing processing ──

        // Handle both new chains format and legacy format
        let ethereumChain, baseChain, polygonChain;

        if (addr.chains) {
            // New format with chains
            ethereumChain = addr.chains.ethereum || {};
            baseChain = addr.chains.base || {};
            polygonChain = addr.chains.polygon || {};
        } else {
            // Legacy format - extract from old backend structure
            // The backend returns: balances, tokenBalances, nativeBalance, transactions, approvals, etc.
            ethereumChain = {
                balances: (() => {
                    // Try to get balances from different possible sources
                    if (addr.balances && Array.isArray(addr.balances)) {
                        return addr.balances;
                    }
                    if (addr.balances && addr.balances.ethereum && addr.balances.ethereum.tokens) {
                        return addr.balances.ethereum.tokens;
                    }
                    if (addr.tokenBalances && addr.tokenBalances.tokens) {
                        return addr.tokenBalances.tokens;
                    }
                    return [];
                })(),
                approvals: addr.approvals || [],
                nftHoldings: addr.nfts || [],
                events: addr.events || [],
                transactionRefs: addr.transactions || []
            };
            baseChain = {
                balances: (() => {
                    if (addr.balances && addr.balances.base && addr.balances.base.tokens) {
                        return addr.balances.base.tokens;
                    }
                    return [];
                })(),
                approvals: [],
                nftHoldings: [],
                events: [],
                transactionRefs: []
            };
            polygonChain = {
                balances: (() => {
                    if (addr.balances && addr.balances.polygon && addr.balances.polygon.tokens) {
                        return addr.balances.polygon.tokens;
                    }
                    return [];
                })(),
                approvals: [],
                nftHoldings: [],
                events: [],
                transactionRefs: []
            };
        }

        // Process balances from new structure
        const processedBalances = {
            ethereum: {
                native: (() => {
                    // Try to get native balance from different possible sources
                    if (ethereumChain.balances && Array.isArray(ethereumChain.balances)) {
                        const nativeBalance = ethereumChain.balances.find(b => (
                            (b.tokenId && b.tokenId === '1:0x0000000000000000000000000000000000000000') ||
                            b.isNative === true || b.contractAddress === null
                        ));
                        if (nativeBalance) {
                            if (nativeBalance.amount) return nativeBalance.amount;
                            if (nativeBalance.balance) return nativeBalance.balance;
                        }
                    }
                    if (addr.balances && addr.balances.ethereum && addr.balances.ethereum.native) {
                        return addr.balances.ethereum.native;
                    }
                    if (addr.nativeBalance && addr.nativeBalance.balance) {
                        return addr.nativeBalance.balance;
                    }
                    if (addr.tokenBalances && addr.tokenBalances.native && addr.tokenBalances.native.balance) {
                        return addr.tokenBalances.native.balance;
                    }
                    return '0x0';
                })(),
                tokens: (() => {
                    // Try to get tokens from different possible sources
                    if (ethereumChain.balances && Array.isArray(ethereumChain.balances)) {
                        return ethereumChain.balances
                            .filter(b => !(
                                (b.tokenId && b.tokenId === '1:0x0000000000000000000000000000000000000000') ||
                                b.isNative === true || b.contractAddress === null
                            ))
                            .filter(b => {
                                const amount = b?.amount ?? b?.balance;
                                if (!amount) return false;
                                if (typeof amount === 'string' && amount.startsWith('0x')) {
                                    try { return BigInt(amount) > 0n; } catch { return false; }
                                }
                                return Number(amount) > 0;
                            });
                    }
                    if (addr.balances && addr.balances.ethereum && addr.balances.ethereum.tokens) {
                        return addr.balances.ethereum.tokens;
                    }
                    if (addr.tokenBalances && addr.tokenBalances.tokens) {
                        return addr.tokenBalances.tokens;
                    }
                    if (addr.balances && Array.isArray(addr.balances)) {
                        return addr.balances.filter(b => b.tokenId !== '1:0x0000000000000000000000000000000000000000');
                    }
                    return [];
                })(),
                // Keep the full balances array including native balance with valueUsd
                allBalances: (() => {
                    if (ethereumChain.balances && Array.isArray(ethereumChain.balances)) {
                        return ethereumChain.balances;
                    }
                    return [];
                })()
            },
            base: {
                native: (() => {
                    if (baseChain.balances && Array.isArray(baseChain.balances)) {
                        const nativeBalance = baseChain.balances.find(b => (
                            (b.tokenId && b.tokenId === '8453:0x0000000000000000000000000000000000000000') ||
                            b.isNative === true || b.contractAddress === null
                        ));
                        if (nativeBalance) {
                            if (nativeBalance.amount) return nativeBalance.amount;
                            if (nativeBalance.balance) return nativeBalance.balance;
                        }
                    }
                    if (addr.balances && addr.balances.base && addr.balances.base.native) {
                        return addr.balances.base.native;
                    }
                    return '0x0';
                })(),
                tokens: (() => {
                    if (baseChain.balances && Array.isArray(baseChain.balances)) {
                        return baseChain.balances
                            .filter(b => !(
                                (b.tokenId && b.tokenId === '8453:0x0000000000000000000000000000000000000000') ||
                                b.isNative === true || b.contractAddress === null
                            ))
                            .filter(b => {
                                const amount = b?.amount ?? b?.balance;
                                if (!amount) return false;
                                if (typeof amount === 'string' && amount.startsWith('0x')) {
                                    try { return BigInt(amount) > 0n; } catch { return false; }
                                }
                                return Number(amount) > 0;
                            });
                    }
                    if (addr.balances && addr.balances.base && addr.balances.base.tokens) {
                        return addr.balances.base.tokens;
                    }
                    return [];
                })(),
                allBalances: (() => {
                    if (baseChain.balances && Array.isArray(baseChain.balances)) {
                        return baseChain.balances;
                    }
                    return [];
                })()
            },
            polygon: {
                native: (() => {
                    if (polygonChain.balances && Array.isArray(polygonChain.balances)) {
                        const nativeBalance = polygonChain.balances.find(b => (
                            (b.tokenId && b.tokenId === '137:0x0000000000000000000000000000000000000000') ||
                            b.isNative === true || b.contractAddress === null
                        ));
                        if (nativeBalance) {
                            if (nativeBalance.amount) return nativeBalance.amount;
                            if (nativeBalance.balance) return nativeBalance.balance;
                        }
                    }
                    if (addr.balances && addr.balances.polygon && addr.balances.polygon.native) {
                        return addr.balances.polygon.native;
                    }
                    return '0x0';
                })(),
                tokens: (() => {
                    if (polygonChain.balances && Array.isArray(polygonChain.balances)) {
                        return polygonChain.balances
                            .filter(b => !(
                                (b.tokenId && b.tokenId === '137:0x0000000000000000000000000000000000000000') ||
                                b.isNative === true || b.contractAddress === null
                            ))
                            .filter(b => {
                                const amount = b?.amount ?? b?.balance;
                                if (!amount) return false;
                                if (typeof amount === 'string' && amount.startsWith('0x')) {
                                    try { return BigInt(amount) > 0n; } catch { return false; }
                                }
                                return Number(amount) > 0;
                            });
                    }
                    if (addr.balances && addr.balances.polygon && addr.balances.polygon.tokens) {
                        return addr.balances.polygon.tokens;
                    }
                    return [];
                })(),
                allBalances: (() => {
                    if (polygonChain.balances && Array.isArray(polygonChain.balances)) {
                        return polygonChain.balances;
                    }
                    return [];
                })()
            }
        };

        // Process approvals from new structure
        const processedApprovals = {
            ethereum: ethereumChain.approvals || [],
            base: baseChain.approvals || [],
            polygon: polygonChain.approvals || []
        };

        // Process NFT holdings and assets from new structure
        const processedNFTs = {
            ethereum: ethereumChain.nftHoldings || [],
            base: baseChain.nftHoldings || [],
            polygon: polygonChain.nftHoldings || []
        };
        const processedNFTAssets = {
            ethereum: ethereumChain.nftAssets || [],
            base: baseChain.nftAssets || [],
            polygon: polygonChain.nftAssets || []
        };

        // Process events from new structure
        const processedEvents = {
            ethereum: ethereumChain.events || [],
            base: baseChain.events || [],
            polygon: polygonChain.events || []
        };

        // Seed each address with last persisted backend transactions
        const recentSeed = Array.isArray(addr.recentTransactions) ? addr.recentTransactions : [];
        recentSeed.forEach((tx) => {
            const chainKey = String(tx.network || tx.chain || 'ethereum').toLowerCase();
            if (!processedEvents[chainKey]) {
                processedEvents[chainKey] = [];
            }
            processedEvents[chainKey].push({
                txHash: tx.txHash,
                hash: tx.txHash,
                from: tx.from,
                to: tx.to,
                network: chainKey,
                chain: chainKey,
                blockNumber: tx.blockNumber,
                valueUsd: tx.valueUsd,
                value: tx.value,
                token: tx.token,
                tokenSymbol: tx.tokenSymbol,
                type: tx.txType || 'transaction_update',
                eventType: tx.txType || 'transaction_update',
                timestamp: tx.timestamp,
            });
        });

        // Calculate total values
        const totalValue = Object.values(processedBalances).reduce((sum, chain) => {
            return sum + chain.tokens.reduce((chainSum, token) => {
                return chainSum + (parseFloat(token.valueUsd) || 0);
            }, 0);
        }, 0);

        // Get latest activity from events
        const allEvents = Object.values(processedEvents)
            .flatMap((events) => (Array.isArray(events) ? events : []))
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        const lastActivity = allEvents.length > 0 ? allEvents[0].timestamp : null;

        // Calculate statistics
        const stats = {
            totalTransactions: allEvents.length,
            ethereumTransactions: processedEvents.ethereum.length,
            baseTransactions: processedEvents.base.length,
            polygonTransactions: processedEvents.polygon.length,
            totalTokens: Object.values(processedBalances).reduce((sum, chain) => sum + chain.tokens.length, 0),
            totalNfts: Object.values(processedNFTs).reduce((sum, chain) => sum + chain.length, 0),
            totalApprovals: Object.values(processedApprovals).reduce((sum, chain) => sum + chain.length, 0),
            totalValue: totalValue,
            lastActivity: lastActivity
        };

        // Calculate native balance
        const nativeBalance = {
            balance: processedBalances.ethereum.native,
            balanceEth: parseInt(processedBalances.ethereum.native, 16) / Math.pow(10, 18) || 0,
            symbol: 'ETH'
        };

        // Apply memory limits to all chain data
        const result = {
            ...addr,
            // New chains structure - trimmed to reduce memory footprint
            chains: {
                ethereum: trimChainData({
                    balances: processedBalances.ethereum.allBalances || [],
                    approvals: processedApprovals.ethereum || [],
                    nftHoldings: processedNFTs.ethereum || [],
                    nftAssets: trimArray(processedNFTAssets.ethereum || [], MAX_NFTS),
                    events: processedEvents.ethereum || [],
                    transactionRefs: ethereumChain.transactionRefs || []
                }),
                base: trimChainData({
                    balances: processedBalances.base.allBalances || [],
                    approvals: processedApprovals.base || [],
                    nftHoldings: processedNFTs.base || [],
                    nftAssets: trimArray(processedNFTAssets.base || [], MAX_NFTS),
                    events: processedEvents.base || [],
                    transactionRefs: baseChain.transactionRefs || []
                }),
                polygon: trimChainData({
                    balances: processedBalances.polygon.allBalances || [],
                    approvals: processedApprovals.polygon || [],
                    nftHoldings: processedNFTs.polygon || [],
                    nftAssets: trimArray(processedNFTAssets.polygon || [], MAX_NFTS),
                    events: processedEvents.polygon || [],
                    transactionRefs: polygonChain.transactionRefs || []
                })
            },
            // Legacy fields - trimmed for backward compatibility
            balances: processedBalances,
            approvals: {
                ethereum: trimArray(processedApprovals.ethereum, MAX_APPROVALS),
                base: trimArray(processedApprovals.base, MAX_APPROVALS),
                polygon: trimArray(processedApprovals.polygon, MAX_APPROVALS)
            },
            nfts: {
                ethereum: trimArray(processedNFTs.ethereum, MAX_NFTS),
                base: trimArray(processedNFTs.base, MAX_NFTS),
                polygon: trimArray(processedNFTs.polygon, MAX_NFTS)
            },
            events: {
                ethereum: trimArray(processedEvents.ethereum, MAX_EVENTS),
                base: trimArray(processedEvents.base, MAX_EVENTS),
                polygon: trimArray(processedEvents.polygon, MAX_EVENTS)
            },
            transactions: trimArray(allEvents, MAX_TRANSACTIONS), // Trimmed events as transactions
            recentTransactions: trimArray(recentSeed, 10),
            totalValue: totalValue,
            lastActivity: lastActivity,
            stats: stats,
            nativeBalance: nativeBalance
        };

        return result;
    };

    // Merge localStorage data with API data for an address
    const mergeAddressDataWithStorage = useCallback((apiData, address) => {
        try {
            const storedData = LocalStorageService.getAddressData(address);
            if (!storedData || !storedData.data) {
                return processAddressData(apiData);
            }

            // Create a merge of API data with localStorage updates
            const mergedData = {
                ...apiData,
                // Update native balance from storage if we have more recent data
                ...(storedData.data.balanceEth && {
                    nativeBalance: {
                        ...apiData.nativeBalance,
                        balanceEth: storedData.data.balanceEth,
                        balance: storedData.data.balance || apiData.nativeBalance?.balance || '0x0'
                    },
                    chains: {
                        ...apiData.chains,
                        ethereum: {
                            ...apiData.chains?.ethereum,
                            // Add recent transactions from storage
                            events: [
                                ...(storedData.data.transactions || []).map(tx => ({
                                    ...tx,
                                    type: 'transaction',
                                    eventType: 'transaction',
                                    timestamp: tx.timestamp
                                })),
                                ...(apiData.chains?.ethereum?.events || [])
                            ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                        }
                    }
                })
            };

            return processAddressData(mergedData);
        } catch (error) {
            console.error('Error merging address data with localStorage:', error);
            return processAddressData(apiData);
        }
    }, []);

    // Load addresses from backend and sync with localStorage
    const loadAddresses = useCallback(async (forceRefresh = false) => {
        // Prevent multiple simultaneous data loading operations with a ref guard
        if (isDataLoadingRef.current && !forceRefresh) {
            return;
        }

        try {
            // Wait for auth to finish loading before making API calls
            if (authLoading) {
                return;
            }
            
            // Check if user is authenticated
            if (!isAuthenticated) {
                setAddresses([]);
                setIsLoading(false);
                return;
            }

            // Consume one-shot bootstrap payload primed by AuthContext.
            if (!forceRefresh && typeof window !== 'undefined' && Array.isArray(window.__BN_BOOTSTRAP_WATCHED_ADDRESSES__)) {
                const bootstrapAddresses = window.__BN_BOOTSTRAP_WATCHED_ADDRESSES__;
                window.__BN_BOOTSTRAP_WATCHED_ADDRESSES__ = null;

                // Guard against partial bootstrap payloads:
                // if chains data is missing, fallback to canonical addresses/all API.
                const hasComprehensiveBootstrapShape = bootstrapAddresses.every((addr) => {
                    const chains = addr?.chains;
                    if (!chains || typeof chains !== 'object') return false;
                    return Boolean(chains.ethereum || chains.base || chains.polygon || chains.bitcoin);
                });

                if (hasComprehensiveBootstrapShape) {
                    const processedBootstrapAddresses = bootstrapAddresses
                        .map(addr => mergeAddressDataWithStorage(addr, addr.address))
                        .filter(Boolean);

                    setAddresses(processedBootstrapAddresses);
                    setLastUpdated(new Date());
                    setIsLoading(false);
                    setIsDataLoading(false);
                    isDataLoadingRef.current = false;

                    LocalStorageService.setAddressesList(processedBootstrapAddresses.map(addr => ({
                        address: addr.address,
                        label: addr.label || addr.name
                    })));
                    return;
                }
            }

            isDataLoadingRef.current = true;
            setIsDataLoading(true);
            // Only the first load (no data yet) blanks pages into skeletons;
            // refreshes keep the current data on screen while fetching.
            if (!hasDataRef.current) setIsLoading(true);
            setError(null);

            // Get all watched addresses with comprehensive data
            const addressListResponse = await addressAPI.getAllWatchedAddresses(forceRefresh);

            // Handle the response structure - backend returns { addresses: [...] }
            const addressList = addressListResponse?.addresses || addressListResponse || [];

            if (!Array.isArray(addressList)) {
                setAddresses([]);
                setLastUpdated(new Date());
                return;
            }

            if (addressList.length === 0) {
                setAddresses([]);
                setLastUpdated(new Date());
                return;
            }

            // Process addresses with new schema structure
            const uniqueAddresses = new Map();
            addressList.forEach(addr => {
                const key = addr.address.toLowerCase();
                if (!uniqueAddresses.has(key)) {
                    uniqueAddresses.set(key, addr);
                }
            });

            // Merge API data with localStorage updates
            const processedAddresses = Array.from(uniqueAddresses.values())
                .map(addr => mergeAddressDataWithStorage(addr, addr.address))
                .filter(Boolean); // Remove any null entries

            // Store data back to localStorage for consistency
            processedAddresses.forEach(addr => {
                LocalStorageService.syncAddressDataFromAPI(addr);
            });

            setAddresses(processedAddresses);
            setLastUpdated(new Date());

            // Update localStorage addresses list for filtering
            LocalStorageService.setAddressesList(processedAddresses.map(addr => ({
                address: addr.address,
                label: addr.label || addr.name
            })));

        } catch (error) {
            console.error('Error in loadAddresses:', error);
            setError(error.message);
            toast.error('Failed to load addresses');

            // Fallback to empty array
            setAddresses([]);
        } finally {
            setIsLoading(false);
            setIsDataLoading(false);
            isDataLoadingRef.current = false;
        }
    }, [isAuthenticated, authLoading, mergeAddressDataWithStorage]);

    // Start progressive updates for an address (with cancellation support)
    const startProgressiveUpdates = async (address) => {
        // Check if user is authenticated
        if (!isAuthenticated) {
            return;
        }

        const addressLower = address.toLowerCase();
        
        // Cancel any existing update loop for this address
        const existingUpdate = activeUpdatesRef.current.get(addressLower);
        if (existingUpdate) {
            existingUpdate.cancelled = true;
        }
        
        // Create cancellation token for this update loop
        const updateToken = { cancelled: false };
        activeUpdatesRef.current.set(addressLower, updateToken);
        
        let attempts = 0;
        const maxAttempts = 10; // More attempts for progressive updates
        const updateInterval = 2000; // Check every 2 seconds

        const updateAddressData = async () => {
            // Check if cancelled before making API call
            if (updateToken.cancelled) return true;
            
            try {
                const comprehensiveData = await addressAPI.getAddressComprehensiveData(
                    address,
                    {
                        approvals: true,
                        balances: true,
                        transactions: true
                    }
                );

                // Check if cancelled after API call
                if (updateToken.cancelled) return true;

                // Update address with new data smoothly (preserve existing state)
                setAddresses(prev => {
                    const updatedAddresses = prev.map(addr => {
                        if (addr.address.toLowerCase() === addressLower) {
                            // Process the new data with the same structure
                            const processedData = processAddressData(comprehensiveData);
                            return {
                                ...addr,
                                ...processedData,
                                isScanning: attempts < maxAttempts - 1 // Keep scanning until last attempt
                            };
                        }
                        return addr;
                    });
                    return updatedAddresses;
                });

                // Check if we have meaningful data across ANY chain
                const chains = comprehensiveData.chains || {};
                const hasData = Object.values(chains).some(chain =>
                    (Array.isArray(chain?.balances) && chain.balances.length > 0) ||
                    (Array.isArray(chain?.events) && chain.events.length > 0) ||
                    (Array.isArray(chain?.transactionRefs) && chain.transactionRefs.length > 0) ||
                    (Array.isArray(chain?.approvals) && chain.approvals.length > 0)
                );

                if (hasData || attempts === maxAttempts - 1) {
                    // Stop scanning
                    setAddresses(prev => prev.map(addr =>
                        addr.address.toLowerCase() === addressLower
                            ? { ...addr, isScanning: false }
                            : addr
                    ));
                    return true; // Success
                }

                return false; // Continue scanning
            } catch (error) {
                // Silently handle errors in progressive updates to avoid console spam
                return false;
            }
        };

        // Start the progressive update loop
        const updateLoop = async () => {
            // Safety timeout - stop scanning after 60 seconds no matter what
            const safetyTimeout = setTimeout(() => {
                setAddresses(prev => prev.map(addr =>
                    addr.address.toLowerCase() === addressLower
                        ? { ...addr, isScanning: false }
                        : addr
                ));
            }, 60000);

            try {
                while (attempts < maxAttempts && !updateToken.cancelled) {
                    attempts++;
                    const success = await updateAddressData();

                    if (success || updateToken.cancelled) {
                        break;
                    }

                    if (attempts < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, updateInterval));
                    }
                }
            } finally {
                clearTimeout(safetyTimeout);
                
                // Always ensure scanning is stopped
                setAddresses(prev => prev.map(addr =>
                    addr.address.toLowerCase() === addressLower
                        ? { ...addr, isScanning: false }
                        : addr
                ));
                
                // Remove from active updates
                activeUpdatesRef.current.delete(addressLower);
            }
        };

        // Start the loop
        updateLoop();
    };
    
    // Cancel all active progressive updates (for cleanup)
    const cancelAllProgressiveUpdates = useCallback(() => {
        activeUpdatesRef.current.forEach((token) => {
            token.cancelled = true;
        });
        activeUpdatesRef.current.clear();
    }, []);

    // Add new address
    const addAddress = async (addressData) => {
        // Bitcoin addresses are case-sensitive (Base58Check) — only lowercase EVM/Solana
        const isBtcAddr = addressData.network === 'bitcoin' ||
            /^(1|3|bc1)/i.test(addressData.address.trim());
        const normalizedAddr = isBtcAddr
            ? addressData.address.trim()
            : addressData.address.toLowerCase();

        try {
            // Check if user is authenticated
            if (!isAuthenticated) {
                return;
            }

            const newAddressData = {
                address: normalizedAddr,
                network: addressData.network || 'ethereum',
                networkId: parseInt(addressData.networkId) || 1,
                chainType: addressData.chainType || 'EVM',
                watchedChains: Array.isArray(addressData.watchedChains) ? addressData.watchedChains : undefined,
                label: addressData.label || addressData.name || '',
                description: addressData.description || ''
            };

            // Optimistic UI update
            const optimisticAddress = isBtcAddr ? {
                ...newAddressData,
                chains: { bitcoin: { balances: [], approvals: [], nftHoldings: [], events: [], transactionRefs: [] } },
                balances: {},
                approvals: {},
                nfts: {},
                events: {},
                transactions: [],
                totalValue: 0,
                lastActivity: null,
                stats: { totalTransactions: 0, totalTokens: 0, totalApprovals: 0, totalValue: 0 },
                isScanning: true,
                nativeBalance: { balance: '0', balanceEth: 0, symbol: 'BTC' }
            } : {
                ...newAddressData,
                balances: { ethereum: { native: '0x0', tokens: [] }, base: { native: '0x0', tokens: [] }, polygon: { native: '0x0', tokens: [] } },
                approvals: { ethereum: [], base: [], polygon: [] },
                nfts: { ethereum: [], base: [], polygon: [] },
                events: { ethereum: [], base: [], polygon: [] },
                transactions: [],
                totalValue: 0,
                lastActivity: null,
                stats: { totalTransactions: 0, totalTokens: 0, totalApprovals: 0, totalValue: 0 },
                isScanning: true,
                nativeBalance: { balance: '0x0', balanceEth: 0, symbol: 'ETH' }
            };

            setAddresses(prev => [...prev, optimisticAddress]);

            // Create address in backend
            const createResponse = await addressAPI.createAddress(newAddressData);

            // If backend already returned comprehensive data, apply it immediately
            if (createResponse?.comprehensiveData?.chains) {
                const dataWithContext = {
                    ...createResponse.comprehensiveData,
                    address: normalizedAddr,
                    network: newAddressData.network,
                    chainType: addressData.chainType,
                };
                const processedData = processAddressData(dataWithContext);
                if (processedData) {
                    const addrLower = normalizedAddr.toLowerCase();
                    setAddresses(prev => prev.map(addr =>
                        addr.address.toLowerCase() === addrLower
                            ? { ...addr, ...processedData, isScanning: false }
                            : addr
                    ));
                    // Data already available, skip polling
                    toast.success(`Address ${formatAddress(newAddressData.address)} added successfully`);

                    setAddresses(prev => {
                        const updatedAddresses = [...prev];
                        LocalStorageService.setAddressesList(updatedAddresses.map(a => ({
                            address: a.address,
                            label: a.label || a.name
                        })));
                        return updatedAddresses;
                    });
                    return;
                }
            }

            // No data yet — kick off polling in the background.
            // Do not block the add flow/spinner on this background sync.
            void startProgressiveUpdates(newAddressData.address);

            toast.success(`Address ${formatAddress(newAddressData.address)} added successfully`);

            // Update localStorage addresses list for filtering
            setAddresses(prev => {
                const updatedAddresses = [...prev];
                LocalStorageService.setAddressesList(updatedAddresses.map(addr => ({
                    address: addr.address,
                    label: addr.label || addr.name
                })));
                return updatedAddresses;
            });

        } catch (error) {
            console.error('Error adding address:', error);

            // Remove optimistic update on error
            const failedAddr = isBtcAddr ? addressData.address.trim() : addressData.address.toLowerCase();
            setAddresses(prev => prev.filter(addr => addr.address !== failedAddr));

            // Show the server's specific validation error if available
            const serverMsg = error.response?.data?.message;
            toast.error(serverMsg || `Failed to add address: ${error.message}`);
            throw error;
        }
    };

    // Update address
    const updateAddress = async (address, updateData) => {
        try {
            // Check if user is authenticated
            if (!isAuthenticated) {
                return;
            }
            const response = await addressAPI.updateAddress(address, updateData);

            setAddresses(prev => prev.map(addr =>
                addr.address.toLowerCase() === address.toLowerCase()
                    ? { ...addr, ...updateData }
                    : addr
            ));

            return response;
        } catch (error) {
            console.error('Error updating address:', error);
            toast.error('Failed to update address');
            throw error;
        }
    };

    // Remove address
    const removeAddress = async (address) => {
        try {
            // Check if user is authenticated
            if (!isAuthenticated) {
                return;
            }
            
            // Cancel any active progressive update for this address
            const addressLower = address.toLowerCase();
            const activeUpdate = activeUpdatesRef.current.get(addressLower);
            if (activeUpdate) {
                activeUpdate.cancelled = true;
                activeUpdatesRef.current.delete(addressLower);
            }
            
            await addressAPI.removeAddress(address);

            setAddresses(prev => prev.filter(addr => addr.address.toLowerCase() !== addressLower));

            toast.success(`Address ${formatAddress(address)} removed successfully`);
        } catch (error) {
            console.error('Error removing address:', error);
            toast.error('Failed to remove address');
            throw error;
        }
    };

    // Refresh all addresses
    const refreshAddresses = async () => {
        try {
            // Check if user is authenticated
            if (!isAuthenticated) {
                return;
            }
            setIsLoading(true);
            await loadAddresses(true); // Force refresh
            toast.success('Addresses refreshed successfully');
        } catch (error) {
            console.error('Error refreshing addresses:', error);
            toast.error('Failed to refresh addresses');
        } finally {
            setIsLoading(false);
        }
    };

    // Refresh single address
    const refreshAddress = async (address) => {
        try {
            // Check if user is authenticated
            if (!isAuthenticated) {
                return;
            }

            const comprehensiveData = await addressAPI.getAddressComprehensiveData(
                address,
                {
                    approvals: true,
                    balances: true,
                    transactions: true
                }
            );

            // Process the new data
            const processedData = processAddressData(comprehensiveData);

            setAddresses(prev => prev.map(addr =>
                addr.address.toLowerCase() === address.toLowerCase()
                    ? { ...addr, ...processedData }
                    : addr
            ));

            toast.success(`Address ${formatAddress(address)} refreshed successfully`);
        } catch (error) {
            console.error('Error refreshing address:', error);
            toast.error('Failed to refresh address');
            throw error;
        }
    };

    // Helper functions
    const getAddress = (address) => {
        return addresses.find(addr => addr.address.toLowerCase() === address.toLowerCase());
    };

    const getAddressesByNetwork = (network) => {
        return addresses.filter(addr => addr.network === network);
    };

    const getTotalPortfolioValue = () => {
        return addresses.reduce((total, addr) => total + (addr.totalValue || 0), 0);
    };

    const getTotalTransactionCount = () => {
        return addresses.reduce((total, addr) => total + (addr.stats?.totalTransactions || 0), 0);
    };

    const getTotalApprovalCount = () => {
        return addresses.reduce((total, addr) => total + (addr.stats?.totalApprovals || 0), 0);
    };

    const getRecentTransactions = (limit = 10) => {
        const allTransactions = addresses.flatMap(addr =>
            (addr.transactions || []).map(tx => ({ ...tx, address: addr.address }))
        );

        return allTransactions
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, limit);
    };

    const getDashboardStats = () => {
        const totalAddresses = addresses.length;
        const totalValue = getTotalPortfolioValue();
        const totalTransactions = getTotalTransactionCount();
        const totalApprovals = getTotalApprovalCount();
        const activeAddresses = addresses.filter(addr => addr.lastActivity).length;

        return {
            totalAddresses,
            totalValue,
            totalTransactions,
            totalApprovals,
            activeAddresses,
            averageValue: totalAddresses > 0 ? totalValue / totalAddresses : 0
        };
    };

    const isDataStale = useCallback(() => {
        if (!lastUpdated) return true;
        const staleThreshold = 5 * 60 * 1000; // 5 minutes
        return Date.now() - lastUpdated.getTime() > staleThreshold;
    }, [lastUpdated]);

    const getCacheAge = () => {
        if (!lastUpdated) return null;
        return Date.now() - lastUpdated.getTime();
    };

    const refreshData = useCallback(async () => {
        if (isDataStale()) {
            await loadAddresses();
        }
    }, [loadAddresses, isDataStale]);

    // Helper function to format address for display
    const formatAddress = (address) => {
        if (!address || typeof address !== 'string' || address.length < 10) {
            return 'Invalid Address';
        }
        return `${address.slice(0, 6)}...${address.slice(-4)}`;
    };

    // Handle localStorage balance and transaction updates
    const handleStorageUpdates = useCallback((address, eventData) => {

        // Only process updates for watched addresses - check and update in one function
        setAddresses(prev => {
            const isWatchedAddress = prev.some(addr =>
                addr.address.toLowerCase() === address.toLowerCase()
            );

            if (!isWatchedAddress) {
                return prev; // Don't process updates for non-watched addresses
            }

            // Process updates for watched addresses only
            const updatedAddresses = prev.map(addr => {
                if (addr.address.toLowerCase() === address.toLowerCase()) {
                    // Get updated data from localStorage
                    const storedData = LocalStorageService.getAddressData(address);
                    console.log('AddressDataContext: Retrieved stored data for', address, {
                        hasData: !!(storedData && storedData.data),
                        balanceEth: storedData?.data?.balanceEth,
                        balance: storedData?.data?.balance,
                        hasChains: !!(storedData?.data?.chains),
                        hasEthereumChain: !!(storedData?.data?.chains?.ethereum),
                        ethereumBalances: storedData?.data?.chains?.ethereum?.balances?.length || 0
                    });
                    if (storedData && storedData.data) {
                        const updatedAddressData = { ...addr };

                        // Update native balance from localStorage
                        if (storedData.data.balanceEth !== undefined && storedData.data.balanceEth !== null) {
                            const nativeBalanceEth = parseFloat(storedData.data.balanceEth);
                            const nativeBalanceWei = storedData.data.balance || '0x0';

                            console.log('AddressDataContext: Updating balance for watched address', address, {
                                storedBalanceEth: nativeBalanceEth,
                                storedBalanceWei: nativeBalanceWei,
                                currentBalanceEth: updatedAddressData.nativeBalance?.balanceEth,
                                currentBalanceWei: updatedAddressData.nativeBalance?.balance
                            });

                            updatedAddressData.nativeBalance = {
                                ...updatedAddressData.nativeBalance,
                                balanceEth: nativeBalanceEth,
                                balance: nativeBalanceWei,
                                symbol: 'ETH'
                            };

                            console.log('AddressDataContext: Updated nativeBalance', {
                                address: address,
                                nativeBalance: updatedAddressData.nativeBalance
                            });

                            // Ensure chains structure exists
                            if (!updatedAddressData.chains) {
                                updatedAddressData.chains = {};
                            }
                            if (!updatedAddressData.chains.ethereum) {
                                updatedAddressData.chains.ethereum = {
                                    balances: [],
                                    approvals: [],
                                    transactionRefs: [],
                                    events: []
                                };
                            }

                            // Update chains structure to reflect balance changes
                            let hasNativeBalance = false;

                            if (updatedAddressData.chains.ethereum.balances && updatedAddressData.chains.ethereum.balances.length > 0) {
                                updatedAddressData.chains.ethereum.balances = updatedAddressData.chains.ethereum.balances.map(b => {
                                    if (b.isNative || b.contractAddress === null ||
                                        (b.tokenId && b.tokenId.includes('0x0000000000000000000000000000000000000000'))) {
                                        hasNativeBalance = true;
                                        // Preserve the original priceUsd if available, otherwise use a fallback
                                        const priceUsd = b.priceUsd || 3500; // Fallback ETH price
                                        const newBalance = {
                                            ...b,
                                            balanceEth: nativeBalanceEth,
                                            amount: nativeBalanceWei,
                                            balance: nativeBalanceWei,
                                            priceUsd: priceUsd, // Preserve price
                                            valueUsd: nativeBalanceEth * priceUsd
                                        };

                                        return newBalance;
                                    }
                                    return b;
                                });
                            }

                            // If no native balance entry exists, create one
                            if (!hasNativeBalance) {
                                const priceUsd = 3500; // Default ETH price
                                updatedAddressData.chains.ethereum.balances.push({
                                    tokenId: '1:0x0000000000000000000000000000000000000000',
                                    isNative: true,
                                    contractAddress: null,
                                    symbol: 'ETH',
                                    name: 'Ether',
                                    decimals: 18,
                                    balanceEth: nativeBalanceEth,
                                    amount: nativeBalanceWei,
                                    balance: nativeBalanceWei,
                                    priceUsd: priceUsd,
                                    valueUsd: nativeBalanceEth * priceUsd
                                });
                            }

                            updatedAddressData.chains = {
                                ...updatedAddressData.chains,
                                ethereum: {
                                    ...updatedAddressData.chains.ethereum
                                }
                            };

                            // Recalculate totalValue
                            const totalValue = Object.values(updatedAddressData.chains || {}).reduce((sum, chain) => {
                                if (chain.balances && Array.isArray(chain.balances)) {
                                    return sum + chain.balances.reduce((chainSum, balance) => {
                                        const direct = balance.valueUsd ? parseFloat(balance.valueUsd) : 0;
                                        return chainSum + direct;
                                    }, 0);
                                }
                                return sum;
                            }, 0);
                            updatedAddressData.totalValue = totalValue;

                            console.log('AddressDataContext: Recalculated totalValue', {
                                address: address,
                                totalValue: totalValue,
                                ethereumBalances: updatedAddressData.chains.ethereum.balances.length
                            });

                            console.log('AddressDataContext: Final address status', {
                                balance: nativeBalanceEth,
                                weiBalance: nativeBalanceWei,
                                totalValue: totalValue,
                                nativeBalanceObject: updatedAddressData.nativeBalance
                            });
                        }

                        // Add new transactions from localStorage
                        if (storedData.data.transactions && Array.isArray(storedData.data.transactions)) {
                            const newTransactions = storedData.data.transactions.slice();

                            if (updatedAddressData.chains?.ethereum) {
                                const existingTransactionHashes = new Set(
                                    updatedAddressData.chains.ethereum.events?.map(event => event.hash || event.txHash) || []
                                );

                                const uniqueNewTransactions = newTransactions.filter(tx => {
                                    const txHash = tx.txHash || tx.hash;
                                    return txHash && !existingTransactionHashes.has(txHash);
                                });

                                if (uniqueNewTransactions.length > 0) {
                                    updatedAddressData.chains = {
                                        ...updatedAddressData.chains,
                                        ethereum: {
                                            ...updatedAddressData.chains.ethereum,
                                            events: [
                                                ...uniqueNewTransactions.map(tx => ({
                                                    ...tx,
                                                    type: 'transaction',
                                                    eventType: 'transaction',
                                                    timestamp: tx.timestamp || new Date().toISOString()
                                                })),
                                                ...(updatedAddressData.chains.ethereum.events || [])
                                            ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
                                            transactionRefs: [
                                                ...uniqueNewTransactions,
                                                ...(updatedAddressData.chains.ethereum.transactionRefs || [])
                                            ].slice(0, 100) // Keep latest 100 transactions
                                        }
                                    };

                                    // Recalculate transaction stats
                                    const totalTransactions = Object.values(updatedAddressData.chains || {}).reduce((sum, chain) => {
                                        return sum + (chain.transactionRefs ? chain.transactionRefs.length : 0);
                                    }, 0);
                                    updatedAddressData.stats = {
                                        ...updatedAddressData.stats,
                                        totalTransactions
                                    };
                                }
                            }
                        }

                        if (process.env.NODE_ENV === 'development') {
                            console.log('AddressDataContext: Updated address data', {
                                address: updatedAddressData.address,
                                balanceEth: updatedAddressData.nativeBalance?.balanceEth,
                                balanceWei: updatedAddressData.nativeBalance?.balance,
                                totalValue: updatedAddressData.totalValue,
                                transactionCount: updatedAddressData.chains?.ethereum?.transactionRefs?.length || 0,
                                approvalCount: updatedAddressData.chains?.ethereum?.approvals?.length || 0,
                                nativeBalanceChain: updatedAddressData.chains?.ethereum?.balances?.find(b => b.isNative || b.contractAddress === null)
                            });
                        }

                        return updatedAddressData;
                    }
                }
                return addr;
            });

            // Log the updated addresses
            if (process.env.NODE_ENV === 'development') {
                console.log('AddressDataContext: Updated addresses:', updatedAddresses.map(a => ({
                    address: a.address,
                    nativeBalance: a.nativeBalance,
                    hasChanged: a.address.toLowerCase() === address.toLowerCase()
                })));
            }

            return updatedAddresses;
        });
    }, []);

    // Load addresses when authentication changes
    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            loadAddresses(); // Normal load, not force refresh
        } else if (!authLoading && !isAuthenticated) {
            setAddresses([]);
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, authLoading]);

    // Listen for localStorage updates from websocket data
    // Use debouncing to prevent rapid-fire updates from causing excessive re-renders
    useEffect(() => {
        const pendingUpdates = new Map();
        let debounceTimer = null;
        
        const processPendingUpdates = () => {
            if (pendingUpdates.size === 0) return;
            
            // Process all pending updates at once
            pendingUpdates.forEach((eventData, address) => {
                handleStorageUpdates(address, eventData);
            });
            pendingUpdates.clear();
        };
        
        const queueUpdate = (address, eventData) => {
            // Queue the update (overwrites previous pending update for same address)
            pendingUpdates.set(address.toLowerCase(), eventData);
            
            // Debounce: process all pending updates after 500ms of no new events
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(processPendingUpdates, 500);
        };
        
        const handleBalanceUpdate = (event) => {
            if (event.detail && event.detail.address) {
                queueUpdate(event.detail.address, event.detail.balanceData);
            }
        };

        const handleTransactionUpdate = (event) => {
            if (event.detail && event.detail.address) {
                queueUpdate(event.detail.address, event.detail.transactionData);
            }
        };

        // Add event listeners for localStorage updates
        window.addEventListener('addressBalanceUpdated', handleBalanceUpdate);
        window.addEventListener('addressTransactionUpdated', handleTransactionUpdate);

        return () => {
            window.removeEventListener('addressBalanceUpdated', handleBalanceUpdate);
            window.removeEventListener('addressTransactionUpdated', handleTransactionUpdate);
            if (debounceTimer) clearTimeout(debounceTimer);
        };
    }, [handleStorageUpdates]);

    // Refresh policy: the backend polls chains for us, so the UI re-pulls
    // the watchlist every 60 s while the tab is visible, pauses when hidden,
    // and refreshes once on focus/visibility if the cache went stale.
    useEffect(() => {
        if (!isAuthenticated) return undefined;

        const REFRESH_MS = 60 * 1000;
        let timerId = null;

        const tick = () => {
            if (document.hidden) return;
            if (!isDataLoadingRef.current) {
                loadAddresses();
            }
        };

        const start = () => {
            if (timerId) return;
            timerId = window.setInterval(tick, REFRESH_MS);
        };

        const stop = () => {
            if (timerId) {
                window.clearInterval(timerId);
                timerId = null;
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                stop();
                return;
            }
            if (isDataStale() && !isDataLoadingRef.current) {
                loadAddresses();
            }
            start();
        };

        const handleFocus = () => {
            if (isDataStale() && !isDataLoadingRef.current) {
                loadAddresses();
            }
        };

        // Run one stale-check on mount for authenticated sessions.
        if (isDataStale() && !isDataLoadingRef.current) {
            loadAddresses();
        }
        if (!document.hidden) start();
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            stop();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [isAuthenticated, loadAddresses, isDataStale]);
    
    // Cleanup: cancel all progressive updates on unmount
    useEffect(() => {
        return () => {
            cancelAllProgressiveUpdates();
        };
    }, [cancelAllProgressiveUpdates]);
    
    // Memory cleanup: flush writes on lifecycle events (no interval loop).
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                LocalStorageService.flushWrites();
            }
        };

        // Clear localStorage on page unload to prevent stale data buildup
        const handleBeforeUnload = () => {
            LocalStorageService.flushWrites();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const value = {
        addresses,
        isLoading,
        isDataLoading,
        error,
        lastUpdated,
        addAddress,
        updateAddress,
        removeAddress,
        refreshAddresses,
        refreshAddress,
        getAddress,
        getAddressesByNetwork,
        getTotalPortfolioValue,
        getTotalTransactionCount,
        getTotalApprovalCount,
        getRecentTransactions,
        getDashboardStats,
        isDataStale,
        getCacheAge,
        refreshData,
        startProgressiveUpdates
    };

    return (
        <AddressDataContext.Provider value={value}>
            {children}
        </AddressDataContext.Provider>
    );
}; 